import { yupResolver } from '@hookform/resolvers/yup';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Autocomplete,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { authContext, Position } from '@staffedupusa/shared-components';
import { MuiFileInput } from 'mui-file-input';
import { FC, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';
import * as yup from 'yup';

import { useGetJobPostings } from '@features/jobPostings/hooks/useGetJobPostings';
import { useEnterpriseCreateApplication } from '@features/applications/hooks/useEnterpriseCreateApplication';
import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';
import { mapPlanStatusAndColor } from '@utils/companyHelper';
import { getStrippedText } from '@utils/helper';

type FormData = {
  job_listing_ids: string;
  first_name: string;
  last_name: string;
  email: string;
  // hired: boolean;
  resume_doc?: File;
  company_id: string;
};

const schema: yup.ObjectSchema<FormData> = yup
  .object({
    job_listing_ids: yup.string().required('Position is required'),
    first_name: yup
      .string()
      .required('First Name is required')
      .test('not-empty-html', 'First Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    last_name: yup
      .string()
      .required('Last Name is required')
      .test('not-empty-html', 'Last Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    email: yup
      .string()
      .required('Email is required')
      .test('is-email', 'Must be a valid email', (value: string) =>
        isEmail(value),
      ),
    // hired: yup.boolean().required(),
    resume_doc: yup.mixed(),
    company_id: yup.string().required('Company is required'),
  })
  .required();

type EnterpriseAddApplicantManuallyFormProps = {
  isHired: boolean;
  onSuccess: (data: { status: number }) => void;
};

const EnterpriseAddApplicantManuallyForm: FC<
  EnterpriseAddApplicantManuallyFormProps
> = ({ isHired, onSuccess }) => {
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | number>(
    '',
  );
  const [companiesData, setCompaniesData] = useState<any[]>([]);

  const { metricsData, isLoadingMetrics } = useGetMetrics({
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId: 'ALL',
  });

  useEffect(() => {
    if (metricsData?.companies) {
      const updatedCompaniesData = mapPlanStatusAndColor(metricsData.companies);
      setCompaniesData(updatedCompaniesData);
    }
  }, [metricsData]);

  const {
    handleSubmit,
    control,
    formState: { errors, touchedFields },
  } = useForm<FormData>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      job_listing_ids: '',
      email: '',
      first_name: '',
      last_name: '',
      // hired: false,
      company_id: '',
    },
  });
  const { createApplication, isCreateApplicationLoading } =
    useEnterpriseCreateApplication({
      setErrorText,
    });

  const { jobPostingsData, isLoadingJobPostings } = useGetJobPostings(
    selectedCompanyId ? selectedCompanyId : null,
  );

  const handleSlotChange = (event: string) => {
    setSelectedCompanyId(event as string);
  };

  const onSubmit = (values: FormData) => {
    createApplication(
      {
        ...values,
        ...(isHired && { hired: isHired }),
        company_Id: selectedCompanyId,
      },
      { onSuccess },
    );
  };

  return (
    <>
      {errorText && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorText}
        </Alert>
      )}
      <form onSubmit={handleSubmit(onSubmit)} data-testid="addApplicantForm">
        <Stack
          spacing={3}
          sx={{
            pt: 1,
            '& p': {
              ml: 1,
              fontSize: '15px',
            },
          }}
        >
          <Controller
            control={control}
            name="first_name"
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                variant="outlined"
                size="small"
                fullWidth
                error={Boolean(errors.first_name)}
                helperText={
                  errors?.first_name ? errors?.first_name.message : ''
                }
                InputProps={{
                  endAdornment: (
                    <>
                      {errors.first_name && (
                        <CancelIcon color="error" fontSize="medium" />
                      )}
                      {touchedFields.first_name && !errors.first_name && (
                        <CheckCircleIcon color="success" fontSize="medium" />
                      )}
                    </>
                  ),
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="last_name"
            render={({ field }) => (
              <TextField
                {...field}
                label="Last Name"
                variant="outlined"
                size="small"
                fullWidth
                error={Boolean(errors.last_name)}
                helperText={errors?.last_name ? errors?.last_name.message : ''}
                InputProps={{
                  endAdornment: (
                    <>
                      {errors.last_name && (
                        <CancelIcon color="error" fontSize="medium" />
                      )}
                      {touchedFields.last_name && !errors.last_name && (
                        <CheckCircleIcon color="success" fontSize="medium" />
                      )}
                    </>
                  ),
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                variant="outlined"
                size="small"
                fullWidth
                error={Boolean(errors.email)}
                helperText={errors?.email ? errors?.email.message : ''}
                InputProps={{
                  endAdornment: (
                    <>
                      {errors.email && (
                        <CancelIcon color="error" fontSize="medium" />
                      )}
                      {touchedFields.email && !errors.email && (
                        <CheckCircleIcon color="success" fontSize="medium" />
                      )}
                    </>
                  ),
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="company_id"
            render={({ field }) => (
              <FormControl error={Boolean(errors.company_id)} fullWidth>
                <Autocomplete
                  options={companiesData}
                  getOptionLabel={(option) =>
                    option && typeof option === 'object'
                      ? `${option.name}, ${option?.s_city}, ${option?.s_state}`
                      : ''
                  }
                  value={
                    companiesData.find((c) => c.company_id === field.value) || null
                  }
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.company_id || '');
                    handleSlotChange(newValue?.company_id || '');
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.company_id === value.company_id
                  }
                  getOptionDisabled={(option) => option.plan_status === 'Expired'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Company"
                      size="small"
                      error={Boolean(errors.company_id)}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.company_id}>
                      {option.name}, {option?.s_city}, {option?.s_state}
                      <span
                        style={{
                          color: '#fff',
                          fontSize: '9px',
                          fontWeight: '600',
                          backgroundColor: option.color,
                          marginLeft: '10px',
                          padding: '1px 5px 2px',
                          borderRadius: 5,
                        }}
                      >
                        {option.plan_status}
                      </span>
                    </li>
                  )}
                />
                {errors?.company_id && (
                  <FormHelperText>{errors.company_id.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="job_listing_ids"
            render={({ field }) => (
              <FormControl error={Boolean(errors.job_listing_ids)}>
                <Select
                  {...field}
                  disabled={!selectedCompanyId}
                  value={isHired ? field.value : field.value.split(',')}
                  size="small"
                  sx={{
                    width: '100%',
                  }}
                  MenuProps={{ PaperProps: { style: { borderRadius: 5 } } }}
                  multiple={!isHired}
                  displayEmpty
                  onChange={(e) =>
                    field.onChange(
                      isHired
                        ? e.target.value
                        : (e.target.value as string[])
                            .filter(Boolean)
                            .join(','),
                    )
                  }
                >
                  <MenuItem disabled value="">
                    <em>{`Select ${isHired ? 'Position' : 'Positions'}`}</em>
                  </MenuItem>
                  {jobPostingsData?.object &&
                    jobPostingsData.object
                      .filter(
                        (item: any) => item.status !== 1 && item.status !== 4,
                      )
                      .map((jobPosting: Position) => (
                        <MenuItem
                          key={jobPosting.id}
                          value={String(jobPosting.id)}
                        >
                          {jobPosting.s_title}
                        </MenuItem>
                      ))}
                </Select>
                {errors?.job_listing_ids && (
                  <FormHelperText>
                    {errors?.job_listing_ids.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
          {selectedCompanyId &&
            jobPostingsData?.object &&
            jobPostingsData?.object?.length === 0 && (
              <Typography variant="body2" color="error">
                No active jobs available. To add a position not listed here,
                move the hiring status from closed to accepting or hiring under
                Job Position management for this location
              </Typography>
            )}
          <Controller
            name="resume_doc"
            control={control}
            render={({ field }) => (
              <>
                <Typography mb={0}>Upload Resume (Optional):</Typography>
                <MuiFileInput
                  {...field}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 5,
                    },
                  }}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    placeholder: 'true',
                    inputProps: {
                      placeholder: 'false',
                    },
                  }}
                />
              </>
            )}
          />
          <LoadingButton
            type="submit"
            startIcon={<DoneIcon />}
            variant="contained"
            size="small"
            fullWidth
            loading={isCreateApplicationLoading}
            loadingPosition="start"
          >
            {`Add ${isHired ? 'New Hire' : 'Applicant'}`}
          </LoadingButton>
        </Stack>
      </form>
    </>
  );
};

export default EnterpriseAddApplicantManuallyForm;
